/**
 * Sidebar bottom user chip — avatar (+ name/plan when expanded), with
 * Integrations and Log out actions always one click away.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Blocks, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { authApi, userProfileApi } from '../../services/api/authApi';
import api from '../../services/api';
import { apiClient } from '../../services/apiClient';
import { storageRemove } from '../../lib/nativeStorage';
import { markIntentionalLogout } from '../../app/loginRedirect';
import { PROFILE_UPDATED_EVENT, type ProfileUpdatedDetail } from '../../lib/profileUpdatedEvent';
import { useChatStore } from '../../stores/chatStore';
import { t } from '../../i18n';

interface SidebarUserFooterProps {
  collapsed: boolean;
}

function formatPlanLabel(plan: string | undefined): string {
  if (!plan || plan.toUpperCase() === 'FREE') return 'Free';
  return plan
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function initialOf(name: string, email: string): string {
  const source = name.trim() || email.trim();
  return source ? source.charAt(0).toUpperCase() : '?';
}

export function SidebarUserFooter({ collapsed }: SidebarUserFooterProps): JSX.Element {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [planLabel, setPlanLabel] = useState('Free');

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const [meRes, profileRes] = await Promise.all([
          authApi.me(),
          userProfileApi.get().catch(() => null),
        ]);
        if (!active) return;
        const user = meRes.data?.user;
        if (user) {
          setName(user.name || '');
          setEmail(user.email || '');
          setAvatar(user.avatar ?? null);
        }
        const profileAvatar = profileRes?.data?.avatar;
        if (profileAvatar) setAvatar(profileAvatar);
        if (profileRes?.data?.name) setName(profileRes.data.name);
        if (profileRes?.data?.email) setEmail(profileRes.data.email);
      } catch {
        // Leave empty defaults — chrome still renders.
      }
    })();

    void (async () => {
      try {
        const json = await apiClient.get<{
          data?: { plan?: string };
          plan?: string;
        }>('/workspaces/me');
        if (!active) return;
        const plan = json.data?.plan ?? json.plan;
        setPlanLabel(formatPlanLabel(plan));
      } catch {
        setPlanLabel('Free');
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onProfileUpdated = (event: Event): void => {
      const detail = (event as CustomEvent<ProfileUpdatedDetail>).detail;
      if (!detail) return;
      if (detail.name !== undefined) setName(detail.name);
      if (detail.avatar !== undefined) setAvatar(detail.avatar);
    };
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
  }, []);

  const handleLogout = (): void => {
    markIntentionalLogout();
    useChatStore.getState().teardown();
    api.setAuthToken(null);
    api.setWorkspaceId(null);
    storageRemove('buzzbit_refresh_token');
    navigate('/login', { replace: true });
  };

  const initial = initialOf(name, email);
  const tooltip = name || email || undefined;
  const displayName = name || email || t('sidebar.account');

  const avatarEl = avatar ? (
    <img
      src={avatar}
      alt=""
      className="h-8 w-8 rounded-full object-cover ring-1 ring-gray-200"
    />
  ) : (
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-900 text-sm font-medium text-white ring-1 ring-gray-200">
      {initial}
    </span>
  );

  const actionClass = cn(
    'inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors',
    'hover:bg-gray-100 hover:text-gray-900',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
  );

  return (
    <div
      className={cn(
        'mt-auto shrink-0 border-t border-gray-200',
        collapsed ? 'space-y-1 p-2' : 'px-3 py-3',
      )}
    >
      <div
        className={cn(
          'flex',
          collapsed ? 'flex-col items-center gap-1' : 'items-center gap-1',
        )}
      >
        <Link
          to="/account"
          title={tooltip}
          aria-label={tooltip ?? t('sidebar.account')}
          className={cn(
            'flex min-w-0 items-center rounded-lg transition-colors',
            'hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
            collapsed ? 'justify-center p-1.5' : 'flex-1 gap-3 px-2 py-1.5',
          )}
        >
          <span className="shrink-0">{avatarEl}</span>
          {!collapsed && (
            <span className="min-w-0 flex-1 text-left">
              <span className="block truncate text-sm font-medium leading-5 text-gray-900">
                {displayName}
              </span>
              <span className="mt-0.5 block truncate text-xs font-medium leading-4 text-gray-500">
                {planLabel}
              </span>
            </span>
          )}
        </Link>
        <div className={cn('flex items-center', collapsed ? 'flex-col gap-1' : 'gap-0.5')}>
          <Link
            to="/account?tab=integrations"
            title={t('sidebar.integrations')}
            aria-label={t('sidebar.integrations')}
            className={actionClass}
          >
            <Blocks className="h-4 w-4" aria-hidden />
          </Link>
          <button
            type="button"
            title={t('sidebar.logOut')}
            aria-label={t('sidebar.logOut')}
            onClick={handleLogout}
            className={cn(actionClass, 'hover:bg-red-50 hover:text-red-600')}
          >
            <LogOut className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
